import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Project, Property } from 'app/modules/projects/projects.types';
import { FieldDef } from '@core/models';
import { GeoJSONSourceOptions } from 'mapbox-gl';
import { AccountState } from '@state/account/state';
import { ReadableFieldPipe } from '@shared/pipes/readable-field.pipe';
import { FieldDefsState } from '@state/fields/state';

export interface MapMarkerLayerData {
  longitude?: string;
  latitude?: string;
  layout: { label: string; value: string }[][];
  title: string;
}

export interface DealMarkerLayerMetaData extends MapMarkerLayerData {
  hex_color: string;
  pipeline_id: number;
  project_id: string;
}

export interface PropertyMarkerLayerData extends MapMarkerLayerData {
  property_id: number;
}

@Injectable({
  providedIn: 'root',
})
export class MapMarkerLayerService {
  private readonly store = inject(Store);
  private readonly readableFieldPipe = inject(ReadableFieldPipe);

  getDealsGeoJSON(
    deals: Project[],
    dealsMapLayoutDefinitions: FieldDef['name'][][],
    fieldDefs: FieldDef[],
  ): GeoJSONSourceOptions['data'] {
    const propertiesWithDealMapLayoutData = deals.map((deal) =>
      deal.properties.map(
        (property: Property & { longitude: string; latitude: string }): DealMarkerLayerMetaData => ({
          hex_color: this.store.selectSnapshot(AccountState.selectAccountColorHexById(deal?.project_stage?.color)),
          longitude: property.longitude,
          latitude: property.latitude,
          pipeline_id: deal.pipeline_id,
          project_id: deal.id,
          title: deal.title,
          layout: {
            ...this.extractMapLayoutDefinitionDataFrom(deal, dealsMapLayoutDefinitions, fieldDefs),
          },
        }),
      ),
    );
    const flattenedMarkerLayerMetaData = propertiesWithDealMapLayoutData.reduce((acc, val) => acc.concat(val), []);

    return {
      type: 'FeatureCollection',
      features: flattenedMarkerLayerMetaData.map((property: DealMarkerLayerMetaData) => ({
        type: 'Feature',
        properties: {
          layout: { ...property.layout },
          pipeline_id: property.pipeline_id,
          project_id: property.project_id,
          title: property.title,
          hex_color: property.hex_color,
        } as Pick<DealMarkerLayerMetaData, 'layout' | 'title' | 'hex_color' | 'pipeline_id' | 'project_id'>,
        geometry: {
          type: 'Point',
          coordinates: [Number(property.longitude), Number(property.latitude)],
        },
      })),
    };
  }

  getPropertiesGeoJSON(
    properties: Property[],
    propertyMapLayoutDefinitions: FieldDef['name'][][],
    fieldDefs: FieldDef[],
  ): GeoJSONSourceOptions['data'] {
    const propertiesWithDealMapLayoutData = properties.map(
      (property: Property & { longitude: string; latitude: string }): PropertyMarkerLayerData => ({
        longitude: property.longitude,
        latitude: property.latitude,
        property_id: property.id,
        title: property.name,
        layout: {
          ...this.extractMapLayoutDefinitionDataFrom(property, propertyMapLayoutDefinitions, fieldDefs),
        },
      }),
    );

    return {
      type: 'FeatureCollection',
      features: propertiesWithDealMapLayoutData.map((property: PropertyMarkerLayerData) => ({
        type: 'Feature',
        properties: {
          layout: { ...property.layout },
          property_id: property.property_id,
          title: property.title,
        } as Pick<PropertyMarkerLayerData, 'layout' | 'title' | 'property_id'>,
        geometry: {
          type: 'Point',
          coordinates: [Number(property.longitude), Number(property.latitude)],
        },
      })),
    };
  }

  private extractMapLayoutDefinitionDataFrom(
    model: Project | Property,
    mapLayoutDefinitions: FieldDef['name'][][],
    fieldDefs: FieldDef[],
  ): { label: string; value: string }[][] {
    return mapLayoutDefinitions?.map((layout) => {
      return layout.map((field) => {
        const fieldValue = model[field];
        const fieldData = fieldDefs.find((fieldDef) => fieldDef?.name === field);
        const fieldLabel = fieldData.label || field;

        return {
          label: fieldLabel,
          value: this.getFormattedValue(fieldData, fieldValue, model),
        };
      });
    });
  }

  /**
   * We need a more abstract service the formats field values. This is a workaround.
   */
  private getFormattedValue(fieldDef: FieldDef, rawValue: any, model: any): string {
    const options = this.store.selectSnapshot(FieldDefsState.getDropdownOptions) ?? {};

    return this.readableFieldPipe.transform(rawValue, model, fieldDef, options);
  }
}
